import type { MessageHandlerDeps } from '../../../../routing/message-handler-deps.js';
import type { ApiToBridgePromptMessage } from '../../../../types/api-to-bridge-messages.js';
import { createBridgePromptSenders } from '../bridge-prompt-wiring.js';
import { parseBridgeAttachments } from './parse-bridge-attachments.js';
import { parseWorktreeBaseBranches } from './parse-worktree-base-branches.js';
import { runPreambleAndPrompt } from './run-preamble-and-prompt.js';

export function handleBridgePrompt(msg: ApiToBridgePromptMessage, deps: MessageHandlerDeps): void {
  const { getWs, log, acpManager, sessionWorktreeManager } = deps;
  const rawPrompt = msg.prompt;
  const promptText =
    typeof rawPrompt === 'string' ? rawPrompt : rawPrompt != null ? String(rawPrompt) : '';
  const attachments = parseBridgeAttachments(msg);
  const sessionId = msg.sessionId as string | undefined;
  const runId = typeof msg.runId === 'string' ? msg.runId : undefined;
  const promptId = typeof msg.id === 'string' ? msg.id : undefined;
  const senders = createBridgePromptSenders(deps, getWs);

  if (!promptText.trim() && attachments.length === 0) {
    log(
      `[Bridge service] Prompt ignored: empty or missing prompt text (session ${typeof msg.sessionId === 'string' ? msg.sessionId.slice(0, 8) : '—'}…, run ${typeof msg.runId === 'string' ? msg.runId.slice(0, 8) : '—'}…).`,
    );
    senders.sendBridgeMessage(
      {
        type: 'prompt_result',
        ...(promptId ? { id: promptId } : {}),
        ...(sessionId ? { sessionId } : {}),
        ...(runId ? { runId } : {}),
        success: false,
        error: "Empty or missing prompt text from the bridge; this turn was not sent to the agent.",
      },
      ['error']
    );
    return;
  }
  const isNewSession = msg.isNewSession === true;
  const rawParent = msg.sessionParent;
  const sessionParent =
    rawParent === 'bridge_root' || rawParent === 'worktrees_root'
      ? rawParent
      : rawParent === 'session_worktrees_root'
        ? ('worktrees_root' as const)
        : null;
  const sessionParentPath =
    typeof msg.sessionParentPath === 'string' && msg.sessionParentPath.trim() ? msg.sessionParentPath.trim() : null;
  const agentType = typeof msg.agentType === 'string' && msg.agentType.trim() ? msg.agentType.trim() : undefined;
  const rawAgentId = (msg as { agentId?: unknown }).agentId;
  const agentId =
    typeof rawAgentId === 'string' && rawAgentId.trim() !== '' ? rawAgentId.trim() : null;
  const mode = typeof msg.mode === 'string' && msg.mode.trim() ? msg.mode.trim() : undefined;
  const agentConfig =
    msg.agentConfig != null && typeof msg.agentConfig === 'object' && !Array.isArray(msg.agentConfig)
      ? (msg.agentConfig as Record<string, unknown>)
      : undefined;
  const worktreeBaseBranches = parseWorktreeBaseBranches(msg);
  acpManager.logPromptReceivedFromBridge({ agentType, mode });

  void sessionWorktreeManager
    .resolveSessionParentPathForPrompt(sessionId, {
      isNewSession,
      sessionParent,
      sessionParentPath,
      ...(worktreeBaseBranches ? { worktreeBaseBranches } : {}),
    })
    .then((cwd) =>
      runPreambleAndPrompt({
        deps,
        msg,
        getWs,
        log,
        sessionWorktreeManager,
        sessionId,
        runId,
        promptText,
        attachments,
        mode,
        agentType,
        agentId,
        agentConfig,
        resolvedCwd: cwd,
        senders,
      }),
    )
    .catch((err) => {
      log(`[Agent] Session parent path resolve failed: ${err instanceof Error ? err.message : String(err)}`);
      void runPreambleAndPrompt({
        deps,
        msg,
        getWs,
        log,
        sessionWorktreeManager,
        sessionId,
        runId,
        promptText,
        attachments,
        mode,
        agentType,
        agentId,
        agentConfig,
        resolvedCwd: undefined,
        senders,
      });
    });
}
